// src/components/TopicManager.tsx
"use client";

import React, { useEffect, useState, useMemo } from "react";
import { Trash2 } from "lucide-react";
import { useAuthContext } from "@/contexts/AuthContext";
import { useRecordContext } from "@/contexts/RecordContext";
import type { Topic, InteractionType } from "@/types/Topic";
import type { CardType } from "@/types/Plan";
import { getEffectivePlan, isOverrideActive } from "@/lib/planMap";

interface TopicManagerProps {
  selectedTopic: Topic | null;
  setSelectedTopic: (topic: Topic) => void;
}

export const TopicManager: React.FC<TopicManagerProps> = ({
  selectedTopic,
  setSelectedTopic,
}) => {
  const { clerkUser } = useAuthContext();
  const currentUserId = clerkUser?.id;

  const recordContext = useRecordContext();

  // === Context Guard ===
  // 2026-07-20: このガードは元々ここ（フックより前）にあったが、PlayArea.tsxと同じ
  // rules-of-hooks違反（早期returnの後にHookが呼ばれる＝Hook数がレンダーごとにズレる）
  // になっていたため、根本修正として全Hookの後（最終JSXのreturn直前）に移動した。
  // ======================

  const selectedCardType = recordContext?.selectedCardType;
  const userProfile = recordContext?.userProfile;

  // === 投稿可否（HowToと思想を揃える：displayName必須） ===
  const hasDisplayName = !!userProfile?.displayName?.trim();
  const canSubmit = true; // 常に押せるようにする

  const [showNameModal, setShowNameModal] = useState(false);
  const [tempDisplayName, setTempDisplayName] = useState("");
  const fetchUserProfile = recordContext?.fetchUserProfile;

  // Discovery 以上かどうか
  // 2026-07-22・CEO指示：穴埋め/テーマ型はDiscovery Pro限定からDiscovery以上に引き下げ済み
  // （既に決定済みの内容を実装に反映するだけの対応。新しい業務判断ではない）。

  const effectivePlan = (getEffectivePlan(userProfile as any) as string) || "";

  const canUseAdvancedTopic =
    effectivePlan === "discovery" ||
    effectivePlan === "discovery_pro" ||
    effectivePlan === "facilitator" ||
    effectivePlan === "partner" ||
    isOverrideActive(userProfile as any);

  const [topics, setTopics] = useState<Topic[]>([]);
  const [newTopicText, setNewTopicText] = useState("");
  // マイリスト（非公開）にするかどうか。2026-07-20〜無料プランでも利用可
  const [isPrivateChoice, setIsPrivateChoice] = useState(false);
  const [mode, setMode] = useState<"list" | "mylist" | "register">("list");
  const [selectedInteractionType, setSelectedInteractionType] =
    useState<InteractionType>("simple");
    const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  // 無料プランの閲覧解放状況（有料プラン・未取得時は null）
  const [browseMeta, setBrowseMeta] = useState<{
    isLimited: boolean;
    othersTotal: number | null;
    othersUnlocked: number | null;
  } | null>(null);

  // === Topics Fetch ===
  const fetchTopics = async () => {
    const res = await fetch("/api/topics?scope=browse", { cache: "no-store" });
    if (res.ok) {
      const data = await res.json();
      const fetched: Topic[] = Array.isArray(data) ? data : data.topics ?? [];
      setTopics(fetched);
      localStorage.setItem("topics", JSON.stringify(fetched));
      setBrowseMeta(
        Array.isArray(data)
          ? null
          : {
              isLimited: !!data.isLimited,
              othersTotal: data.othersTotal ?? null,
              othersUnlocked: data.othersUnlocked ?? null,
            }
      );
    }
  };

  useEffect(() => {
    const cached = localStorage.getItem("topics");
    if (cached) setTopics(JSON.parse(cached));
    fetchTopics();
  }, []);

  const submitTopic = async (authorNameOverride?: string) => {
    if (!newTopicText.trim()) return;

    // 2026-07-20: 非公開（マイリスト）は無料プランでも作成可能にした（相談役相談前の整理のため）
    const requestedVisibility: "public" | "private" =
      isPrivateChoice ? "private" : "public";

    const newTopic: Omit<Topic, "id"> = {
      topic: newTopicText.trim(),
      topicType: "",
      interactionType: selectedInteractionType,
      createdAt: new Date().toISOString(),
      planAccess: ["discovery", "discovery_pro", "facilitator", "partner"],
      uid: currentUserId ?? "guest",
      authorName:
      authorNameOverride ??
      userProfile?.displayName ??
      tempDisplayName ??
      "",
      usage_count: 0,
      visibility: requestedVisibility,
      registeredEdition:
        (userProfile?.registeredEdition as CardType) ||
        ("standard" as CardType),
      cardType: [selectedCardType || "standard"],
      category: "",
    };

    try {
      const res = await fetch("/api/topics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newTopic),
      });
      if (!res.ok) throw new Error("登録に失敗しました");

      await res.json();
      setNewTopicText("");

      // 投稿数で解放件数が変わるため、一覧を取り直す
      await fetchTopics();
    } catch (error) {
      console.error("❌ API経由でお題登録失敗:", error);
    }
  };

  const handleDeleteTopic = async (topicId?: string) => {
    if (!topicId) return;
    if (!window.confirm("このお題を削除しますか？")) return;

    try {
      const res = await fetch(`/api/topics/${topicId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("削除に失敗しました");
      setTopics((prev) => prev.filter((t) => t.id !== topicId));
    } catch (error) {
      console.error("❌ お題削除失敗:", error);
      alert("削除に失敗しました");
    }
  };

  // === Add Topic Wrapper ===
const handleAddTopic = async () => {
  if (!newTopicText.trim()) return;

  // displayName未登録ならモーダルへ
  if (!hasDisplayName) {
    setShowNameModal(true);
    return;
  }

  await submitTopic();
};

const handleSaveDisplayName = async () => {
  const name = tempDisplayName.trim();
  if (!name) return;

  await fetch("/api/user-profile", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      displayName: name,
    }),
  });

  // ① まずモーダルを閉じる
  setShowNameModal(false);
  setTempDisplayName("");

  // ② Contextを更新（安全のため）
  await fetchUserProfile?.();

  // ③ 🔥 override付きで直接投稿（ここが超重要）
  await submitTopic(name);
};

  // === Interaction Type Options ===
  const interactionTypeOptions = [
    { label: "通常", value: "simple" },
    { label: "穴埋め", value: "fillInTheBlank" },
    { label: "テーマ型", value: "theme" },
  ];

  // === Filtered Topics ===
  const filteredTopics = useMemo(
    () =>
      topics.filter(
        (t) =>
          t.interactionType === selectedInteractionType &&
          t.displayRole !== "daily" // ← dailyを一覧から除外
      ),
    [topics, selectedInteractionType]
  );

  // === マイリスト（自分が作ったお題を可視性を問わず全部並べる。ルーム作成分も含む。
  // interactionTypeタブとは無関係） ===
  const myAllTopics = useMemo(
    () => topics.filter((t) => !!currentUserId && t.uid === currentUserId),
    [topics, currentUserId]
  );

  // === Context Guard（全Hookの後・最終JSXの直前）===
  if (!recordContext)
    return <div style={{ color: "red" }}>Context未提供</div>;
  if (recordContext.userProfileLoading || !recordContext.userProfile)
    return <div className="text-gray-400">ユーザー情報を取得中...</div>;
  // ======================

  return (
    <div className="p-2 bg-gray-50 rounded-md">

      {/* ▼ ▼ ▼ タブ（リスト / 登録）— /account と完全統一 ▼ ▼ ▼ */}
      <div className="flex border-b mb-3">
        {(["list", "mylist", "register"] as const).map((tab) => {
          const isActive = mode === tab;
          const label = tab === "list" ? "リスト" : tab === "mylist" ? "マイリスト" : "登録";
          return (
            <button
              key={tab}
              onClick={() => setMode(tab)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-all
                ${
                  isActive
                    ? "border-gray-900 text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              {label}
            </button>
          );
        })}
      </div>
      {/* ▲▲▲ タブ UI 統一終了 ▲▲▲ */}

      {/* ▼ ▼ ▼ インタラクションタイプタブ（マイリストでは関係ないので非表示） ▼ ▼ ▼ */}
      {mode !== "mylist" && (
      <div className="flex border-b mb-4">
        {interactionTypeOptions.map((option) => {
          const isActive = selectedInteractionType === option.value;
          const isAdvanced =
            option.value === "fillInTheBlank" || option.value === "theme";
          const isLocked = isAdvanced && !canUseAdvancedTopic;

          return (
            <div key={option.value}>
              <button
                type="button"
                onClick={() => {
                  if (isLocked) return;
                  setSelectedInteractionType(option.value as InteractionType);
                }}
                onMouseEnter={(e) => {
                  if (!isLocked) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTooltipPos({
                    x: rect.left + rect.width / 2,
                    y: rect.top - 8,
                  });
                }}
                onMouseLeave={() => setTooltipPos(null)}
                aria-disabled={isLocked}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-all
                  ${
                    isActive
                      ? "border-gray-900 text-gray-900"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }
                  ${isLocked ? "opacity-50 cursor-not-allowed" : ""}
                `}
              >
                {option.label}
              </button>

              {/* ▼ ロック時 tooltip ▼ */}
            </div>
          );
        })}
      </div>
      )}

      {/* ▼ viewport 基準 tooltip（overflow 無関係） */}
      {tooltipPos && (
        <div
          className="fixed z-50 px-3 py-1 text-xs text-white bg-gray-900 rounded shadow"
          style={{
            left: tooltipPos.x,
            top: tooltipPos.y,
            transform: "translateX(-50%) translateY(-100%)",
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          この機能は、現在のご利用内容ではお使いいただけません。
        </div>
      )}

      {/* ▲▲▲ インタラクションタブ 統一終了 ▲▲▲ */}

      {mode === "list" || mode === "mylist" ? (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {(mode === "mylist" ? myAllTopics : filteredTopics).length === 0 ? (
            <p className="text-sm text-gray-400">
              {mode === "mylist" ? "まだ非公開のお題がありません。" : "お題がまだありません。"}
            </p>
          ) : (
            (mode === "mylist" ? myAllTopics : filteredTopics).map((topic) => {
              const isSelected =
                selectedTopic?.id === topic.id;
              const isMine = !!currentUserId && topic.uid === currentUserId;

              return (
                <div key={topic.id} className="flex items-stretch gap-1">
                  <button
                    onClick={() => {
                      setSelectedTopic(topic);
                      recordContext.log("topic_change", { topic: topic.topic });
                    }}
                    className={`flex-1 min-w-0 text-left px-3 py-2 rounded border text-sm transition ${
                      isSelected
                        ? "bg-blue-100 border-blue-400 text-blue-900 font-semibold"
                        : "bg-white border-gray-200 hover:bg-gray-100 text-gray-800"
                    }`}
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="line-clamp-2 break-keep">
                        {topic.topic}
                      </span>
                      <div className="flex items-center justify-end gap-1 mt-1">
                        {isMine && topic.visibility === "private" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">
                            非公開
                          </span>
                        )}
                        {isMine && topic.visibility === "room" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">
                            ルーム作成
                          </span>
                        )}
                        {topic.authorName && (
                          <span className="text-xs text-gray-500">
                            {topic.authorName}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                  {isMine && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTopic(topic.id);
                      }}
                      title="削除する"
                      aria-label="削除する"
                      className="shrink-0 flex items-center justify-center w-9 rounded border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-300"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              );
            })
          )}
          {mode === "list" &&
            browseMeta?.isLimited &&
            browseMeta.othersTotal != null &&
            browseMeta.othersUnlocked != null &&
            browseMeta.othersUnlocked < browseMeta.othersTotal && (
              <p className="text-xs text-gray-400 text-center pt-1">
                あと{browseMeta.othersTotal - browseMeta.othersUnlocked}件。お題を投稿すると、もっと見られるようになります。
              </p>
            )}
        </div>
      ) : (
        <div className="space-y-2">
          {selectedInteractionType === "fillInTheBlank" ? (
            <textarea
              value={newTopicText}
              onChange={(e) => setNewTopicText(e.target.value)}
              rows={2}
              placeholder="空欄箇所は __(半角2つ) または ＿（全角1つ）"
              className="w-full border rounded px-2 py-1 text-sm"
            />
          ) : (
            <input
              type="text"
              value={newTopicText}
              onChange={(e) => setNewTopicText(e.target.value)}
              placeholder="新しいお題を入力"
              className="w-full border rounded px-2 py-1 text-sm"
            />
          )}

          {/* ▼ 公開/非公開トグル（2026-07-20: マイリストは無料プランでも利用可に変更） */}
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={isPrivateChoice}
              onChange={(e) => setIsPrivateChoice(e.target.checked)}
            />
            自分だけが見られる非公開のお題にする
          </label>

          {/* ▼ 緑ボタンに統一 */}
          <button
            onClick={handleAddTopic}
            className="px-4 py-2 rounded text-sm bg-green-600 text-white hover:bg-green-700"
          >
            登録する
          </button>
        </div>
      )}
      {showNameModal && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">

      <h2 className="text-lg font-bold mb-3">
        投稿するには表示名の登録が必要です。
      </h2>

      <input
        type="text"
        value={tempDisplayName}
        onChange={(e) => setTempDisplayName(e.target.value)}
        className="w-full border rounded-lg px-3 py-2 mb-4"
        placeholder="表示名を入力してください"
      />

      <div className="flex justify-end gap-2">
        <button
          onClick={() => setShowNameModal(false)}
          className="px-4 py-2 rounded-lg bg-gray-200"
        >
          キャンセル
        </button>

        <button
          onClick={handleSaveDisplayName}
          className="px-4 py-2 rounded-lg bg-green-600 text-white"
        >
          保存して続ける
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default TopicManager;
